/**
 * Performance Utilities
 * Provides performance monitoring and optimization utilities.
 */

/**
 * Performance metrics
 */
export interface PerformanceMetrics {
  /** Current FPS */
  fps: number;
  /** Average FPS */
  averageFps: number;
  /** Frame count */
  frameCount: number;
}

/**
 * FPS Monitor class
 */
class FPSMonitor {
  private frameTimes: number[] = [];
  private lastFrameTime: number = performance.now();
  private frameCount: number = 0;
  private maxFrameHistory: number = 60; // Keep last 60 frames

  /**
   * Record a frame
   */
  recordFrame(): number {
    const now = performance.now();
    const delta = now - this.lastFrameTime;
    this.lastFrameTime = now;
    this.frameCount++;

    this.frameTimes.push(delta);
    if (this.frameTimes.length > this.maxFrameHistory) {
      this.frameTimes.shift();
    }

    return delta > 0 ? 1000 / delta : 0; // FPS
  }

  /**
   * Get current FPS
   */
  getFPS(): number {
    if (this.frameTimes.length === 0) {
      return 0;
    }
    const averageFrameTime =
      this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    return averageFrameTime > 0 ? 1000 / averageFrameTime : 0;
  }

  /**
   * Get average FPS
   */
  getAverageFPS(): number {
    return this.getFPS();
  }

  /**
   * Get frame count
   */
  getFrameCount(): number {
    return this.frameCount;
  }

  /**
   * Reset metrics
   */
  reset(): void {
    this.frameTimes = [];
    this.frameCount = 0;
    this.lastFrameTime = performance.now();
  }

  /**
   * Get metrics
   */
  getMetrics(): PerformanceMetrics {
    return {
      fps: this.getFPS(),
      averageFps: this.getAverageFPS(),
      frameCount: this.frameCount,
    };
  }
}

/**
 * Global FPS monitor instance
 */
let fpsMonitor: FPSMonitor | null = null;

/**
 * Get or create FPS monitor instance
 */
export function getFPSMonitor(): FPSMonitor {
  if (!fpsMonitor) {
    fpsMonitor = new FPSMonitor();
  }
  return fpsMonitor;
}

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function
 * @param func - Function to throttle
 * @param limit - Limit time in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

